import { Menu } from "lucide-react";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { useAdminNavBadges } from "../../hooks/queries/useAdminNavBadges";
import { AdminSidebar } from "./admin-sidebar";

type AdminLayoutProps = {
	children: ReactNode;
};

export function AdminLayout({ children }: AdminLayoutProps) {
	// Fetched here, not in the sidebar: AdminLayout already sits inside the
	// QueryClientProvider, and this keeps AdminSidebar presentational.
	const navBadges = useAdminNavBadges();
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const menuButtonRef = useRef<HTMLButtonElement>(null);

	const openSidebar = useCallback(() => {
		setSidebarOpen(true);
	}, []);

	const closeSidebar = useCallback(() => {
		setSidebarOpen(false);
		menuButtonRef.current?.focus();
	}, []);

	return (
		<div className="flex h-screen bg-background-base">
			{/* Skip to main content link */}
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background-elevated focus:text-foreground-default focus:rounded-md focus:shadow-lg focus:top-4 focus:left-4"
			>
				Skip to main content
			</a>

			{/* Mobile sidebar overlay */}
			{sidebarOpen && (
				<button
					type="button"
					aria-label="Close sidebar overlay"
					className="fixed inset-0 z-40 bg-black/50 lg:hidden cursor-default"
					onClick={closeSidebar}
				/>
			)}

			{/* Sidebar */}
			<AdminSidebar
				badges={navBadges}
				isOpen={sidebarOpen}
				onClose={closeSidebar}
			/>

			{/* Main content */}
			<div
				className="flex flex-1 flex-col overflow-hidden"
				aria-hidden={sidebarOpen || undefined}
			>
				{/* Mobile header */}
				<header className="flex h-14 items-center gap-4 border-b border-border-default bg-background-elevated px-4 lg:hidden">
					<button
						ref={menuButtonRef}
						type="button"
						onClick={openSidebar}
						aria-label="Open sidebar"
						aria-expanded={sidebarOpen}
						className="p-2 -ml-2 text-foreground-muted hover:text-foreground-default hover:bg-background-muted rounded-md"
					>
						<Menu className="h-6 w-6" />
					</button>
					<span className="font-semibold text-foreground-default">
						Admin Panel
					</span>
				</header>

				{/* Page content */}
				<main id="main-content" className="flex-1 overflow-auto">
					<div className="p-4 sm:p-6 lg:p-8">{children}</div>
				</main>
			</div>
		</div>
	);
}
